import { IKimSendPayload, IKimSendResponse, IVzdSearchParams, IVzdSearchResult } from '@alberta/konexi-shared';
import { Injectable } from '@angular/core';
import { AuthService } from 'src/app/shared/services/auth.service';
import { Branch, EnvironmentService } from 'src/app/shared/services/environment/environment.service';

const KIM_DEV_DEFAULT_RECIPIENT = 'alberta@rise-support.kim.telematik-test';

/** Verzeichnisdienst nicht erreichbar (Netzwerkfehler oder 502 vom KIM-Service). */
export class VzdUnavailableError extends Error {
  constructor(message = 'VZD unavailable') {
    super(message);
    this.name = 'VzdUnavailableError';
  }
}

export class KimSendError extends Error {
  constructor(
    public readonly status: number | null,
    message: string
  ) {
    super(message);
    this.name = 'KimSendError';
  }

  /** Netzwerkfehler oder Gateway-Fehler – ein erneuter Versuch kann helfen. */
  public get isRetriable(): boolean {
    return this.status === null || this.status >= 500 || this.status === 502;
  }
}

/**
 * HTTP-Client für den KIM-Microservice (ti.services kim.service).
 */
@Injectable({ providedIn: 'root' })
export class KimApiService {
  constructor(
    private _authService: AuthService,
    private _environmentService: EnvironmentService
  ) {}

  public async searchVzd(params: IVzdSearchParams): Promise<IVzdSearchResult> {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && `${value}`.trim() !== '') {
        query.set(key, `${value}`);
      }
    }

    let response: Response;
    try {
      response = await fetch(`${this._environmentService.kimServiceUrl}/v1/vzd/search?${query.toString()}`, {
        method: 'GET',
        headers: await this.buildHeaders(),
      });
    } catch (error) {
      throw new VzdUnavailableError(error?.message);
    }

    if (response.status === 502 || response.status === 503 || response.status === 504) {
      throw new VzdUnavailableError(`VZD search failed with status ${response.status}`);
    }
    if (!response.ok) {
      throw new KimSendError(response.status, `VZD search failed with status ${response.status}`);
    }

    return response.json();
  }

  public async send(payload: IKimSendPayload): Promise<IKimSendResponse> {
    const effectivePayload = this.checkAndApplyDevRecipientOverride(payload);

    let response: Response;
    try {
      response = await fetch(`${this._environmentService.kimServiceUrl}/v1/send`, {
        method: 'POST',
        headers: await this.buildHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(effectivePayload),
      });
    } catch (error) {
      throw new KimSendError(null, error?.message ?? 'network error');
    }

    if (!response.ok) {
      throw new KimSendError(response.status, `KIM send failed with status ${response.status}`);
    }

    return response.json();
  }

  private checkAndApplyDevRecipientOverride(payload: IKimSendPayload): IKimSendPayload {
    if (this._environmentService.branch !== Branch.dev) {
      return payload;
    }

    console.log(
      `KIM send: recipient "${payload.to}" overwritten with test address ` +
        `"${KIM_DEV_DEFAULT_RECIPIENT}" because the app is running on DEV`
    );
    return { ...payload, to: KIM_DEV_DEFAULT_RECIPIENT };
  }

  private async buildHeaders(additionalHeaders: Record<string, string> = {}): Promise<Record<string, string>> {
    const accessToken = await this._authService.getAccessToken();
    return {
      Authorization: `Bearer ${accessToken}`,
      ...additionalHeaders,
    };
  }
}
